/* eslint-disable @typescript-eslint/no-magic-numbers */
import { palette as b2bDarkPalette } from './b2b-dark-palette';
import { palette as b2bPalette } from './b2b-palette';
import {
  legacyBorderRadius,
  legacyDarkPalette,
  legacyLightPalette,
  legacyTypography,
} from './b2c-legacy';
import { defaultBorderRadius, defaultFontSize, defaultSpacing, defaultTypography } from './defaults';
import type { Typography } from './defaults';
import { palette as paramDarkPalette } from './param-dark-palette';
import { palette as paramPalette } from './param-palette';
import { legacyPresentation, modernPresentation } from './presentation';
import type { Presentation, PresentationOverrides } from './presentation';
import { shadows } from './shadows';

// ── Palette sub‑types ──────────────────────────────────────────────────

export type Additional = {
  accentElement: string;
};
export type Background = {
  main: string;
  accent: string;
  colorAccent: string;
};
export type Disabled = {
  main: string;
  accent: string;
  /** Figma TDS: subtle disabled fill */
  lowContrast: string;
  /** Figma TDS: stronger disabled fill */
  mediumContrast: string;
};
export type Error = {
  main: string;
  mediumContrast: string;
  lowContrast: string;
};
export type Ghost = {
  main: string;
  mediumContrast: string;
  lowContrast: string;
};
export type Icons = {
  main: string;
  logo: string;
};
export type Overlay = {
  main: string;
  accent: string;
  tooltip: string;
};
// Contrast scale mirrors Figma TDS Foundations (max → reversed). Legacy keys
// (`mainContrast`, `mediumContrast`, tertiary `main`/`upperMediumContrast`/…)
// are retained as aliases so existing component reads keep working — see
// FOUNDATIONS-GAP.md §1.
export type Primary = {
  maxContrast: string;
  highContrast: string;
  upperMidContrast: string;
  midContrast: string;
  lowerMidContrast: string;
  lowContrast: string;
  reversedContrast: string;
  /** @deprecated alias of `highContrast` */
  mainContrast: string;
  /** @deprecated alias of `lowerMidContrast` */
  mediumContrast: string;
};
export type Tertiary = {
  maxContrast: string;
  highContrast: string;
  upperMidContrast: string;
  midContrast: string;
  lowerMidContrast: string;
  lowContrast: string;
  reversedContrast: string;
  /** @deprecated alias of `highContrast` */
  main: string;
  /** @deprecated alias of `upperMidContrast` */
  upperMediumContrast: string;
  /** @deprecated alias of `midContrast` */
  mediumContrastAlternative: string;
  /** @deprecated legacy tertiary mid value (no Figma equivalent) */
  mediumContrast: string;
};
export type Secondary = {
  maxContrast: string;
  highContrast: string;
  upperMidContrast: string;
  midContrast: string;
  lowerMidContrast: string;
  lowContrast: string;
  reversedContrast: string;
  /** @deprecated alias of `highContrast` */
  mainContrast: string;
  /** @deprecated alias of `lowerMidContrast` */
  mediumContrast: string;
};
export type Accent = {
  maxContrast: string;
  highContrast: string;
  lowerMidContrast: string;
  lowContrast: string;
  surfaceElement: string;
};
export type Success = {
  main: string;
  mediumContrast: string;
  lowContrast: string;
};
export type Warning = {
  main: string;
  mediumContrast: string;
  lowContrast: string;
};
export type Text = {
  title: string;
  body: string;
  description: string;
  caption: string;
  titleReversed: string;
  disabled: string;
  emptyState: string;
};
export type Transparent = {
  background: string;
  main: string;
};

export type Purple = {
  50: string;
  100: string;
  200: string;
  400: string;
  500: string;
};
export type Turquoise = {
  100: string;
  200: string;
  400: string;
  500: string;
};
export type Green = {
  100: string;
  200: string;
  500: string;
};
export type Orange = {
  100: string;
  200: string;
  500: string;
};
export type Red = {
  100: string;
  200: string;
  500: string;
};
export type Core = {
  black: string;
  white: string;
  grey: string;
  lightGrey: string;
  transparentWhite: string;
  transparentBlack: string;
};
export type DeepPurple = {
  100: string;
  200: string;
  300: string;
  600: string;
  700: string;
  800: string;
  900: string;
  transparent900: string;
};

export type DeepGrey = {
  100: string;
  200: string;
  300: string;
  600: string;
  700: string;
  800: string;
  900: string;
  transparent900: string;
};

type CategoryItem = {
  main: string;
  background: string;
};
export type Category = {
  cash: CategoryItem;
  deals: CategoryItem;
  electronics: CategoryItem;
  entertainment: CategoryItem;
  fashion: CategoryItem;
  financial: CategoryItem;
  foodDrinks: CategoryItem;
  groceries: CategoryItem;
  health: CategoryItem;
  houseGarden: CategoryItem;
  householdBill: CategoryItem;
  kids: CategoryItem;
  onlineShopping: CategoryItem;
  others: CategoryItem;
  pets: CategoryItem;
  sport: CategoryItem;
  subscription: CategoryItem;
  transfers: CategoryItem;
  travel: CategoryItem;
  transport: CategoryItem;
};

export type Theme = {
  additional: Additional;
  background: Background;
  disabled: Disabled;
  error: Error;
  ghost: Ghost;
  icons: Icons;
  overlay: Overlay;
  primary: Primary;
  tertiary: Tertiary;
  secondary: Secondary;
  accent: Accent;
  success: Success;
  text: Text;
  transparent: Transparent;
  warning: Warning;
  deepPurple: DeepPurple;
  deepGrey: DeepGrey;
  turquoise: Turquoise;
  green: Green;
  orange: Orange;
  red: Red;
  purple: Purple;
  core: Core;
  category: Category;
};

// ── Token types ────────────────────────────────────────────────────────

export type Spacing = typeof defaultSpacing;
export type BorderRadius = Record<keyof typeof defaultBorderRadius, string>;
export type FontSize = typeof defaultFontSize;
export type Shadows = typeof shadows;
export type { Typography } from './defaults';

// ── Component override types ───────────────────────────────────────────
// Each atom can be themed at the component level.
// Keys listed here match the atom directory names in PascalCase.

export type AccordionOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type ActionableFieldOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type AlertDialogOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type AmountOverrides = {
  fontSize?: string;
};

export type AppBarOverrides = {
  height?: string;
  spacing?: string;
};

export type AvatarOverrides = {
  borderRadius?: string;
  size?: string;
};

export type BackButtonOverrides = {
  size?: string;
};

export type BoxOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type ButtonOverrides = {
  borderRadius?: Partial<Record<'small' | 'medium' | 'large', string>>;
  fontSize?: Partial<Record<'small' | 'medium' | 'large', string>>;
  spacing?: Partial<Record<'small' | 'medium' | 'large', string>>;
};

export type CircleLoaderOverrides = {
  size?: string;
};

export type ContainerOverrides = {
  maxWidth?: string;
  spacing?: string;
};

export type CopyToClipboardOverrides = {
  fontSize?: string;
};

export type CssBaselineOverrides = {
  fontFamily?: string;
  fontSize?: string;
};

export type CurrencyInputBaseOverrides = {
  fontSize?: string;
  spacing?: string;
};

export type DividerOverrides = {
  spacing?: string;
  color?: string;
};

export type DropdownOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type DueInTagOverrides = {
  borderRadius?: string;
  fontSize?: string;
};

export type FileInputOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type FilePreviewOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type FlexOverrides = {
  gap?: string;
};

export type FormOverrides = {
  spacing?: string;
};

export type FormGroupOverrides = {
  spacing?: string;
};

export type FormHelperTextOverrides = {
  fontSize?: string;
  spacing?: string;
};

export type FormatOverrides = {
  fontSize?: string;
};

export type HeadlineOverrides = {
  fontSize?: string;
  spacing?: string;
};

export type IconCircleOverrides = {
  size?: string;
  borderRadius?: string;
};

export type InfiniteScrollOverrides = {
  spacing?: string;
};

export type InputOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type InputBaseOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type InputLabelOverrides = {
  fontSize?: string;
  spacing?: string;
};

export type InputRangeGroupOverrides = {
  spacing?: string;
};

export type ItemRowOverrides = {
  spacing?: string;
  borderRadius?: string;
};

export type LayoutOverrides = {
  spacing?: string;
  maxWidth?: string;
};

export type LinkOverrides = {
  fontSize?: string;
};

export type LoaderOverrides = {
  size?: string;
};

export type LogoOverrides = {
  size?: string;
};

export type MarkdownOverrides = {
  fontSize?: string;
  spacing?: string;
};

export type ModalOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type NativeSelectOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type NotificationOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type NumberIconOverrides = {
  size?: string;
  fontSize?: string;
};

export type PaginationOverrides = {
  spacing?: string;
  borderRadius?: string;
};

export type PaperOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type PasswordStrengthOverrides = {
  spacing?: string;
  fontSize?: string;
};

export type PdfDocumentViewerOverrides = {
  spacing?: string;
};

export type PieChartOverrides = {
  size?: string;
};

export type ProgressOverrides = {
  borderRadius?: string;
  size?: string;
};

export type RadioButtonOverrides = {
  size?: string;
  spacing?: string;
};

export type RemainingChartOverrides = {
  size?: string;
};

export type RepaymentOverrides = {
  spacing?: string;
  fontSize?: string;
  borderRadius?: string;
};

export type SkeletonOverrides = {
  borderRadius?: string;
};

export type SkeletonFieldOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type SliderOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type TableOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type TagOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type TagCheckboxOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type TextAreaOverrides = {
  borderRadius?: string;
  fontSize?: string;
  spacing?: string;
};

export type ToastOverrides = {
  borderRadius?: string;
  spacing?: string;
};

export type ToggleOverrides = {
  size?: string;
  borderRadius?: string;
};

export type TooltipOverrides = {
  borderRadius?: string;
  spacing?: string;
  fontSize?: string;
};

export type TypographyOverrides = {
  fontSize?: Partial<
    Record<
      | 'h1'
      | 'h1Mobile'
      | 'h2'
      | 'h3'
      | 'b1'
      | 'b2'
      | 'b3'
      | 'b4'
      | 'b5'
      | 'bodyMedium'
      | 'bodyMediumMobile',
      string
    >
  >;
  fontWeight?: Partial<Record<'regular' | 'medium' | 'heavy', number>>;
};

export type UserAvatarOverrides = {
  size?: string;
  borderRadius?: string;
  fontSize?: string;
};

export type ComponentOverrides = {
  Accordion?: AccordionOverrides;
  ActionableField?: ActionableFieldOverrides;
  AlertDialog?: AlertDialogOverrides;
  Amount?: AmountOverrides;
  AppBar?: AppBarOverrides;
  Avatar?: AvatarOverrides;
  BackButton?: BackButtonOverrides;
  Box?: BoxOverrides;
  Button?: ButtonOverrides;
  CircleLoader?: CircleLoaderOverrides;
  Container?: ContainerOverrides;
  CopyToClipboard?: CopyToClipboardOverrides;
  CssBaseline?: CssBaselineOverrides;
  CurrencyInputBase?: CurrencyInputBaseOverrides;
  Divider?: DividerOverrides;
  Dropdown?: DropdownOverrides;
  DueInTag?: DueInTagOverrides;
  FileInput?: FileInputOverrides;
  FilePreview?: FilePreviewOverrides;
  Flex?: FlexOverrides;
  Form?: FormOverrides;
  FormGroup?: FormGroupOverrides;
  FormHelperText?: FormHelperTextOverrides;
  Format?: FormatOverrides;
  Headline?: HeadlineOverrides;
  IconCircle?: IconCircleOverrides;
  InfiniteScroll?: InfiniteScrollOverrides;
  Input?: InputOverrides;
  InputBase?: InputBaseOverrides;
  InputLabel?: InputLabelOverrides;
  InputRangeGroup?: InputRangeGroupOverrides;
  ItemRow?: ItemRowOverrides;
  Layout?: LayoutOverrides;
  Link?: LinkOverrides;
  Loader?: LoaderOverrides;
  Logo?: LogoOverrides;
  Markdown?: MarkdownOverrides;
  Modal?: ModalOverrides;
  NativeSelect?: NativeSelectOverrides;
  Notification?: NotificationOverrides;
  NumberIcon?: NumberIconOverrides;
  Pagination?: PaginationOverrides;
  Paper?: PaperOverrides;
  PasswordStrength?: PasswordStrengthOverrides;
  PdfDocumentViewer?: PdfDocumentViewerOverrides;
  PieChart?: PieChartOverrides;
  Progress?: ProgressOverrides;
  RadioButton?: RadioButtonOverrides;
  RemainingChart?: RemainingChartOverrides;
  Repayment?: RepaymentOverrides;
  Skeleton?: SkeletonOverrides;
  SkeletonField?: SkeletonFieldOverrides;
  Slider?: SliderOverrides;
  Table?: TableOverrides;
  Tag?: TagOverrides;
  TagCheckbox?: TagCheckboxOverrides;
  TextArea?: TextAreaOverrides;
  Toast?: ToastOverrides;
  Toggle?: ToggleOverrides;
  Tooltip?: TooltipOverrides;
  Typography?: TypographyOverrides;
  UserAvatar?: UserAvatarOverrides;
};

// ── Theme mode & brand ─────────────────────────────────────────────────

export type ThemeModes = 'light' | 'dark';

/**
 * Brand preset selecting the base palette. b2c (Param) is the default; b2b
 * swaps in a grayscale primary/tertiary + teal accent palette (same token
 * names). `config.palette` still merges on top of whichever brand is chosen.
 */
export type Brand = 'b2c' | 'b2b' | 'b2c-legacy';

const brandPalettes: Record<Brand, Record<ThemeModes, Theme>> = {
  b2c: { light: paramPalette, dark: paramDarkPalette },
  b2b: { light: b2bPalette, dark: b2bDarkPalette },
  'b2c-legacy': { light: legacyLightPalette, dark: legacyDarkPalette },
};

const presentations = {
  b2c: modernPresentation,
  b2b: modernPresentation,
  'b2c-legacy': legacyPresentation,
} satisfies Record<Brand, typeof modernPresentation>;

// ── ThemeConfig — the input to getTheme ────────────────────────────────

export type ThemeConfig = {
  /** Informational label for the theme */
  themeName: string;
  /** Font family for the entire app (default: 'Param') */
  fontFamily?: string;
  /** Light palette overrides — merged over default param palette */
  palette?: Partial<Theme>;
  /** Dark palette overrides — merged over default param dark palette */
  darkPalette?: Partial<Theme>;
  /** Spacing token overrides */
  spacing?: Partial<Spacing>;
  /** Border-radius token overrides */
  borderRadius?: Partial<BorderRadius>;
  /** Font-size token overrides */
  fontSize?: Partial<FontSize>;
  /** Typography token overrides — fontFamily, fontWeight scale, per-style metrics. */
  typography?: Partial<Typography>;
  /** Shadow overrides */
  shadows?: Partial<Shadows>;
  /** Per-component theming overrides */
  components?: ComponentOverrides;
  /** Shared presentation, applied after the preset and foundation overrides. */
  presentation?: PresentationOverrides;
};

// ── ResolvedTheme — the output of getTheme ─────────────────────────────

export type ResolvedTheme = {
  themeName: string;
  fontFamily: string;
  mode: ThemeModes;
  brand: Brand;
  palette: Theme;
  shadows: Shadows;
  spacing: Spacing;
  borderRadius: BorderRadius;
  fontSize: FontSize;
  typography: Typography;
  components: ComponentOverrides;
  presentation: Presentation;
};

// ── Deep merge helper ──────────────────────────────────────────────────

type AnyRecord = Record<string, unknown>;

function cloneThemeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(cloneThemeValue);
  }
  if (value !== null && typeof value === 'object') {
    return deepMerge(value as AnyRecord, {});
  }

  return value;
}

function deepMerge<T extends AnyRecord>(base: T, overrides: Partial<T>): T {
  const result = { ...base };

  // Each resolved preset owns its nested tokens; callers cannot mutate defaults.
  for (const key of Object.keys(base) as Array<keyof T>) {
    result[key] = cloneThemeValue(base[key]) as T[keyof T];
  }

  for (const key of Object.keys(overrides) as Array<keyof T>) {
    const overrideValue = overrides[key];

    if (
      overrideValue !== undefined &&
      typeof overrideValue === 'object' &&
      overrideValue !== null &&
      !Array.isArray(overrideValue)
    ) {
      const baseValue = base[key];
      const baseObject =
        baseValue !== null && typeof baseValue === 'object' && !Array.isArray(baseValue)
          ? (baseValue as AnyRecord)
          : {};
      result[key] = deepMerge(baseObject, overrideValue as AnyRecord) as T[keyof T];
    } else if (overrideValue !== undefined) {
      result[key] = cloneThemeValue(overrideValue) as T[keyof T];
    }
  }

  return result;
}

// ── getTheme ───────────────────────────────────────────────────────────

export const getTheme = ({
  mode,
  brand,
  config,
}: {
  mode?: ThemeModes;
  brand?: Brand;
  config?: ThemeConfig;
}): ResolvedTheme => {
  const requiredMode = mode ?? 'light';
  const requiredBrand = brand ?? 'b2c';
  const baseTypography = requiredBrand === 'b2c-legacy' ? legacyTypography : defaultTypography;
  const baseBorderRadius =
    requiredBrand === 'b2c-legacy' ? legacyBorderRadius : defaultBorderRadius;

  const basePalette = brandPalettes[requiredBrand][requiredMode];
  const paletteOverrides = requiredMode === 'dark' ? config?.darkPalette : config?.palette;

  const resolvedPalette = deepMerge(
    basePalette as AnyRecord,
    (paletteOverrides ?? {}) as AnyRecord
  ) as Theme;

  const resolvedTypography: Typography = config?.typography
    ? (deepMerge(baseTypography as AnyRecord, config.typography as AnyRecord) as Typography)
    : (deepMerge(baseTypography as AnyRecord, {}) as Typography);
  // config.fontFamily (legacy top-level override) wins over typography.fontFamily
  const resolvedFontFamily = config?.fontFamily ?? resolvedTypography.fontFamily;

  const resolved: Omit<ResolvedTheme, 'presentation'> = {
    themeName: config?.themeName ?? requiredBrand,
    fontFamily: resolvedFontFamily,
    mode: requiredMode,
    brand: requiredBrand,
    palette: resolvedPalette,
    shadows: config?.shadows
      ? (deepMerge(shadows as AnyRecord, config.shadows as AnyRecord) as Shadows)
      : { ...shadows },
    spacing: config?.spacing
      ? ({ ...defaultSpacing, ...config.spacing } satisfies Spacing)
      : { ...defaultSpacing },
    borderRadius: config?.borderRadius
      ? ({ ...baseBorderRadius, ...config.borderRadius } satisfies BorderRadius)
      : { ...baseBorderRadius },
    fontSize: config?.fontSize
      ? ({ ...defaultFontSize, ...config.fontSize } satisfies FontSize)
      : { ...defaultFontSize },
    typography:
      resolvedFontFamily !== resolvedTypography.fontFamily
        ? { ...resolvedTypography, fontFamily: resolvedFontFamily }
        : resolvedTypography,
    components: config?.components ?? {},
  };

  return {
    ...resolved,
    presentation: deepMerge(
      presentations[requiredBrand](resolved) as AnyRecord,
      (config?.presentation ?? {}) as AnyRecord
    ) as Presentation,
  };
};
